import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface SessionInfoField {
  label: string;
  value: string | number;
  tooltip?: string;
  color?: string;
  bold?: boolean;
  showForStatus?: string | number | (string | number)[]; // Show field only for these status(es)
}

interface SessionInfoDisplayProps {
  statusMessage?: string;
  statusColor?: string;
  fields: SessionInfoField[];
  currentStatus?: string | number; // Current status to match against showForStatus
}

const SessionInfoDisplay: React.FC<SessionInfoDisplayProps> = ({
  statusMessage,
  statusColor,
  fields,
  currentStatus,
}) => {
  const { t } = useTranslation();

  // Filter fields based on showForStatus
  const visibleFields = fields.filter((field) => {
    if (!field.showForStatus || currentStatus === undefined) {
      return true; // Show field if no showForStatus specified
    }
    const statusesToShow = Array.isArray(field.showForStatus)
      ? field.showForStatus
      : [field.showForStatus];
    return statusesToShow.includes(currentStatus);
  });

  return (
    <Box sx={{ p: 2, bgcolor: 'background.grey', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        {t('Session Information')}
      </Typography>
      
      {statusMessage && statusColor && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              bgcolor: `${statusColor}15`,
              border: `1.5px solid ${statusColor}`,
              borderRadius: 1,
              px: 1.5,
              py: 1,
              maxWidth: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: statusColor,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                lineHeight: 1.4,
              }}
            >
              {statusMessage}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {visibleFields.map((field, index) => {
          const content = (
            <Typography variant="body2" key={index}>
              <strong>{field.label}</strong>{' '}
              <span
                style={{
                  color: field.color,
                  fontWeight: field.bold ? 600 : 'normal',
                }}
              >
                {field.value}
              </span>
            </Typography>
          );

          return field.tooltip ? (
            <Tooltip key={index} title={field.tooltip}>
              {content}
            </Tooltip>
          ) : (
            content
          );
        })}
      </Box>
    </Box>
  );
};

export default SessionInfoDisplay;
